import Icon from '../../../components/AppIcon';
import React from 'react';
import { motion } from 'framer-motion';

const WhyChooseUsSection = () => {
	return (
		<section className='pt-20 bg-gradient-to-br from-gray-50 via-white to-gray-100 relative overflow-hidden'>
			{/* Background Pattern */}
			<div className='absolute inset-0 opacity-5'>
				<div className='absolute top-0 left-0 w-full h-full bg-gradient-to-r from-primary/10 via-transparent to-accent/10'></div>
				<div className='absolute top-20 right-10 w-32 h-32 bg-primary/5 rounded-full blur-3xl'></div>
				<div className='absolute bottom-20 left-10 w-40 h-40 bg-accent/5 rounded-full blur-3xl'></div>
			</div>

			<div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10'>
				{/* Header */}
				<motion.div
					initial={{ opacity: 0, y: 30 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.8 }}
					viewport={{ once: true }}
					className='text-center mb-16'
				>
					<h2 className='font-playfair font-bold text-4xl lg:text-5xl text-primary mb-6'>
						Why Choose Us
					</h2>
					<p className='font-source text-xl text-text-secondary max-w-3xl mx-auto'>
						we are a team of innovative lawyers with years of experience and professionalism to meet
						the unique needs of our clients.
					</p>
				</motion.div>

				{/* Mission & Vision */}
				<div className='grid lg:grid-cols-2 gap-12 mb-16'>
					{/* Mission */}
					<motion.div
						initial={{ opacity: 0, x: -50 }}
						whileInView={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.8 }}
						viewport={{ once: true }}
						className='bg-white rounded-2xl p-8 shadow-xl border border-gray-100 hover:shadow-2xl transition-all duration-300'
					>
						<div className='flex items-center mb-6'>
							<div className='w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mr-4 shadow-lg'>
								<Icon name='Target' size={32} color='white' />
							</div>
							<h3 className='font-playfair font-bold text-2xl text-primary'>Our Mission</h3>
						</div>
						<p className='font-source text-lg text-text-secondary leading-relaxed'>
							Our mission is to provide exceptional legal services with unwavering dedication,
							integrity and client satisfaction. We are committed to providing innovative and
							client-focused legal services that set a new benchmark in efficiency and
							professionalism. We stay ahead of industry changes, offering personalized,
							forward-thinking solutions that anticipate and meet the evolving needs of our clients.
						</p>
					</motion.div>

					{/* Vision */}
					<motion.div
						initial={{ opacity: 0, x: 50 }}
						whileInView={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.8 }}
						viewport={{ once: true }}
						className='bg-white rounded-2xl p-8 shadow-xl border border-gray-100 hover:shadow-2xl transition-all duration-300'
					>
						<div className='flex items-center mb-6'>
							<div className='w-16 h-16 bg-accent rounded-2xl flex items-center justify-center mr-4 shadow-lg'>
								<Icon name='Eye' size={32} color='white' />
							</div>
							<h3 className='font-playfair font-bold text-2xl text-accent'>Our Vision</h3>
						</div>
						<p className='font-source text-lg text-text-secondary leading-relaxed'>
							To be a leading corporate and commercial law firm, recognized for delivering
							exceptional legal services that shape the future of business success and inspire
							unwavering trust across industries through innovation, integrity, experience and
							excellent service delivery that meets the needs of our clients.
						</p>
					</motion.div>
				</div>

				{/* Core Values */}
				<motion.div
					initial={{ opacity: 0, y: 30 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.8 }}
					viewport={{ once: true }}
					className='text-center mb-16'
				>
					<h3 className='font-playfair font-bold text-3xl text-primary mb-8'>Our Core Values</h3>
					<div className='grid grid-cols-2 md:grid-cols-5 gap-6'>
						{['Integrity', 'Excellence', 'Commitment', 'Experience', 'Innovation'].map(
							(value, index) => (
								<motion.div
									key={value}
									initial={{ opacity: 0, scale: 0.8 }}
									whileInView={{ opacity: 1, scale: 1 }}
									transition={{ duration: 0.5, delay: index * 0.1 }}
									viewport={{ once: true }}
									className='bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 border border-gray-100 group'
								>
									<div className='w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300'>
										<Icon name='Star' size={24} color='white' />
									</div>
									<h4 className='font-source font-bold text-primary text-lg'>{value}</h4>
								</motion.div>
							)
						)}
					</div>
				</motion.div>
			</div>

			{/* Bottom CTA - Full Width */}
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				whileInView={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.6 }}
				viewport={{ once: true }}
				className='w-full'
			>
				<div className='relative overflow-hidden bg-gray-900 min-h-[300px] flex items-center justify-center'>
					{/* Dark Legal Background */}
					<div className='absolute inset-0 bg-gradient-to-r from-gray-800 via-gray-900 to-gray-800'></div>

					{/* Legal Items Pattern */}
					<div className='absolute inset-0 opacity-20'>
						<div className='absolute top-1/2 left-1/4 transform -translate-y-1/2 w-32 h-32 bg-gray-700 rounded-lg rotate-12'></div>
						<div className='absolute top-1/2 right-1/4 transform -translate-y-1/2 w-24 h-24 bg-gray-600 rounded-full'></div>
						<div className='absolute top-1/3 left-1/2 transform -translate-x-1/2 w-16 h-16 bg-gray-700 rounded-lg'></div>
					</div>

					{/* Dark Overlay */}
					<div className='absolute inset-0 bg-black/40'></div>

					{/* Content */}
					<div className='relative z-10 max-w-5xl mx-auto px-8'>
						<h3 className='font-source font-bold text-2xl md:text-3xl lg:text-4xl text-white uppercase tracking-wide leading-tight mb-4'>
							<span className='block text-center'>CLIENT SATISFACTION AND BEST INTEREST</span>
							<span className='block text-red-500 text-center'> IS OUR TOP PRIORITY</span>
						</h3>
						<p className='font-source text-base md:text-lg text-gray-300 mb-8 max-w-2xl mx-auto text-center'>
							Join thousands of satisfied clients who trust us with their legal matters
						</p>
						<div className='flex flex-col sm:flex-row gap-4 justify-center'>
							<motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
								<a
									href='tel:+2349095529557'
									className='inline-flex items-center px-8 py-3 bg-white text-gray-900 font-source font-bold rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl text-base'
								>
									<Icon name='Phone' size={20} className='mr-2' />
									Call Now: (+234) 909-552-9557
								</a>
							</motion.div>
							<motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
								<a
									href='/contact'
									className='inline-flex items-center px-8 py-3 border-2 border-white text-white font-source font-bold rounded-lg hover:bg-white hover:text-gray-900 transition-all duration-300 shadow-lg hover:shadow-xl text-base'
								>
									<Icon name='Calendar' size={20} className='mr-2' />
									Schedule a Consultation
								</a>
							</motion.div>
						</div>
					</div>
				</div>
			</motion.div>
		</section>
	);
};

export default WhyChooseUsSection;
